import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  Divider,
  IconButton,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { AutomationsContext } from "contexts/Automations";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import {
  Automation,
  AutomationStatus,
  BulkSendSettingsStatus,
  OrganizationBulkSendSettings,
  OrganizationBulkSendSettingsVersion,
  OrganizationNiche,
} from "schemas/dashboard";

import { Template } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { ON_BEHALF_OF_TOKEN } from "utils/localStorage";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

import CreateAutomationDialog from "./CreateAutomationDialog";
import UpcomingPitchScheduleDialog from "./UpcomingPitchScheduleDialog";
import styles from "./styles";

const AutomationVA = () => {
  const isAdmin =
    Boolean(sessionStorage.getItem(ON_BEHALF_OF_TOKEN)) ||
    process.env.NODE_ENV === "development";

  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { getToken } = useAuth();
  const { currentOrg, profile, organizationVaProfile } = useContext(
    OrganizationUserContext,
  );

  const defaultSettings = {
    defaultNumberOfBrands: 100,
    numberOfRepitchBrands: 0,
    status: BulkSendSettingsStatus.active,
    personalizeOutreach: organizationVaProfile?.personalizeOutreach,
    version: OrganizationBulkSendSettingsVersion.v3,
  };

  const { categoriesById } = useContext(BentoCategoriesContext);
  const { automations, setAutomations, fetchLoading, fetchAutomations } =
    useContext(AutomationsContext);
  const { setErrorAlert, setAlert } = useContext(AlertContext);

  const [open, setOpen] = useState(false);
  const [niches, setNiches] = useState<OrganizationNiche[]>([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [automationId, setAutomationId] = useState<number>(-1);
  const [openPitchDialog, setOpenPitchDialog] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settings, setSettings] =
    useState<OrganizationBulkSendSettings>(defaultSettings);
  const [selectedTemplates, setSelectedTemplates] = useState<Template[]>([]);

  const openUpcomingPitch = () => {
    trackEvent("Schedule Upcoming Pitches Dialog Opened");
    setOpenPitchDialog(true);
  };

  const handleClosePitchDialog = () => {
    setOpenPitchDialog(false);
  };

  const closeCreateDialog = () => {
    setAutomationId(-1);
    setOpenCreate(false);
  };

  const closeBothDialogs = () => {
    closeCreateDialog();
    setOpen(false);
  };

  const handleCreateSearch = () => {
    setOpenCreate(true);
    trackEvent("Create Automation Button Clicked");
  };

  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bulk-send-settings`,
        "GET",
      );
      if (res.bulkSendSetting) {
        setSettings(res.bulkSendSetting);
        setSelectedTemplates(res.bulkSendSetting?.templates || []);
      } else {
        setSettings(defaultSettings);
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchOrganizationNiches = async () => {
    try {
      const { niches } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches/v2`,
        "GET",
      );
      setNiches(niches);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const saveAutomation = async (automation: Automation) => {
    if (!automation.id) return;
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/automations/${automation.id}`,
        automation?.id ? "PUT" : "POST",
        {},
        automation,
      );
      setAutomations((prev) => {
        const copy = makeDeepCopy(prev);
        const index = copy?.findIndex(
          (x: Automation) => x.id === res.automation?.id,
        );
        if (index === -1) {
          copy.push(res.automation);
        } else {
          copy[index] = res.automation;
        }
        return copy;
      });
      setAlert("Successfully updated your automation", "success");
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleIsTopNicheToggle = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    toggledAutomationId: number,
  ) => {
    event.stopPropagation();
    const automationToChange = makeDeepCopy(
      automations.find((a) => a.id === toggledAutomationId),
    );
    automationToChange.isTopNiche = !automationToChange.isTopNiche;
    saveAutomation(automationToChange);
  };

  const handleIsPausedToggle = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    toggledAutomationId: number,
  ) => {
    event.stopPropagation();
    const automationToChange = makeDeepCopy(
      automations.find((a) => a.id === toggledAutomationId),
    );
    if (automationToChange.status === AutomationStatus.active) {
      automationToChange.status = AutomationStatus.inactive;
    } else {
      automationToChange.status = AutomationStatus.active;
    }
    saveAutomation(automationToChange);
  };

  const total = automations.reduce((sum: number, item: Automation) => {
    return item.status === "active" ? sum + item.count : sum;
  }, 0);

  useEffect(() => {
    if (currentOrg?.id && isAdmin) {
      if (!settings.id) {
        fetchSettings();
      }
      fetchOrganizationNiches();
      fetchAutomations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return (
    <>
      {isAdmin && (
        <Button
          disableElevation
          size="small"
          sx={{ mr: 1, borderRadius: "8px" }}
          variant="outlined"
          onClick={() => setOpen(true)}
        >
          Automations ✨
        </Button>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobileScreen}
      >
        <DialogTitle sx={styles.header}>
          <Box sx={{ flexGrow: 1 }}>My Automated Searches (Total: {total})</Box>

          <IconButton sx={styles.close} onClick={() => setOpen(false)}>
            <Box component="i" className="fa-regular fa-xmark" />
          </IconButton>
        </DialogTitle>
        {fetchLoading && (
          <Box alignItems="center" sx={{ px: 2, pb: 2 }}>
            Loading your collections...{" "}
            <CircularProgress size={20} sx={{ ml: 1 }} />
          </Box>
        )}
        <Box>
          <Divider />
          {automations?.map((automation, index) => (
            <Box
              key={index}
              sx={styles.automationRow}
              onClick={() => setAutomationId(automation.id || -1)}
            >
              <Box key={index} sx={styles.border}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography sx={{ fontSize: 14 }}>
                    {automation?.searchParameters?.category_ids?.[0]
                      ? categoriesById[
                          parseInt(
                            automation?.searchParameters?.category_ids?.[0],
                          )
                        ]?.name ||
                        automation?.searchParameters?.category_ids?.[0]
                      : "All"}
                  </Typography>

                  <Typography sx={{ fontSize: 12 }}>
                    Search: {automation?.searchParameters?.query},
                    Autogenerated: {automation.isAutogenerated ? "Yes" : "No"}
                  </Typography>
                </Box>

                <Switch
                  checked={automation.status === AutomationStatus.inactive}
                  onClick={(e) => handleIsPausedToggle(e, automation.id!)}
                  color="primary"
                />
                <Typography sx={{ mr: 3 }}>Is Paused</Typography>

                <Switch
                  checked={automation.isTopNiche}
                  onClick={(e) => handleIsTopNicheToggle(e, automation.id!)}
                  color="primary"
                />
                <Typography sx={{ mr: 3 }}>Is Top Niche</Typography>

                {automation.id && (
                  <Box>
                    <Box component="i" className="fa-regular fa-arrow-right" />
                  </Box>
                )}
              </Box>
            </Box>
          ))}

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Button
              size="small"
              onClick={handleCreateSearch}
              sx={{ my: 1, ml: 1 }}
            >
              + Create New Automation
            </Button>
            <Box>
              {settingsLoading ? (
                <CircularProgress size={14} />
              ) : settings.id &&
                settings.status === BulkSendSettingsStatus.active ? (
                <Box
                  component="i"
                  sx={styles.bulkScheduleActive}
                  className="fa-solid fa-circle"
                />
              ) : (
                <Box
                  component="i"
                  sx={styles.bulkScheduleInactive}
                  className="fa-solid fa-circle"
                />
              )}
              <Button
                size="small"
                sx={{ mr: 1 }}
                disabled={settingsLoading}
                onClick={openUpcomingPitch}
              >
                Schedule Upcoming Pitches
              </Button>
            </Box>
          </Box>
          <Divider />
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box />
            <Button
              size="small"
              target="_blank"
              rel="noreferrer"
              href={`https://docs.google.com/document/d/${profile?.onboardingGoogleDocId}`}
              sx={{ my: 1, mr: 1 }}
              disabled={!profile || !profile.onboardingGoogleDocId}
            >
              Google Doc Notes
            </Button>
          </Box>
        </Box>

        {(openCreate || automationId > 0) && (
          <CreateAutomationDialog
            open={automationId > 0 || openCreate}
            handleClose={closeCreateDialog}
            editAutomation={automations?.find((x) => x.id === automationId)}
            closeBothDialogs={closeBothDialogs}
            niches={niches}
            fetchOrganizationNiches={fetchOrganizationNiches}
          />
        )}

        <UpcomingPitchScheduleDialog
          open={openPitchDialog}
          handleClose={handleClosePitchDialog}
          settings={settings}
          setSettings={setSettings}
          setSelectedTemplates={setSelectedTemplates}
          selectedTemplates={selectedTemplates}
        />
      </Dialog>
    </>
  );
};

export default AutomationVA;
